import { Vue, Component, Prop, Inject, InjectReactive, Watch } from 'vue-property-decorator';
import {
  Step, StepPager, Btn, Toolbar, FlashCard, ILoanImport, IStepComponent, RestartFunction, Grid, Field, TextField,
  GetService, IMapping, IMappingDetail, SetMapping, GetUserFunction, Splitter, IDataPreviewError, IGridComponent, NextStepFunction,
  SetFileSource, IFileSource, Action, GetContextMenuItemsParams, MenuItemDef, SelectionChangedEvent, GetRowIdFunc
} from '@samc/centricity-core';
import { IKeyValue, groupBy, AjaxHelper } from '@samc/common';
import '@/registerVuePlugins';
import XLSX from '@samc/sheetjs-style';

@Component({
  name: 'import-enrichment-step',
  components: {
    Step, StepPager, Btn, Toolbar, FlashCard, Grid, Field, TextField, Splitter, Action
  }
})
export default class ImportEnrichmentStep extends Vue implements IStepComponent {
  public errorGrid(): IGridComponent | undefined { return this.$refs.errorGrid as any; }

  public isValid: boolean = true;
  public isDirty: boolean = false;
  public isReadonly: boolean = false;

  public description: string = '';
  public isImportDisabled: boolean = false;
  public errorMessages: IKeyValue<string, IDataPreviewError[]>[] = [];
  public selectedErrorMessages: IDataPreviewError[] = [];
  public selectedErrorGridKey = 0;
  public errorGridKey = 0;
  public panes = [.25, .75];

  public existingLoanFound: boolean | null = null;
  public insertCount: number = 0;
  public updateCount: number = 0;
  public deleteCount: number = 0;
  public loanListCache: any[] = [];
  public duplicateLoanDataCache: any[] = [];
  public getRowId: GetRowIdFunc<IKeyValue<string, IDataPreviewError[]>> = x => x.data.key;

  @Prop({ required: true }) public scratchPadUrl!: string;
  @Prop({ default: '' }) public packageId!: string;
  @Prop({ default: null }) public enrichment!: any;
  @Prop({ default: null }) public validation!: any;
  @Prop({ default: '' }) public scratchPadNode!: string;
  @Prop() public value!: string;
  @Prop({ default: 'Import Enriched Data' }) public title!: string;
  @Prop({ type: Boolean, default: false }) public isEnriched!: boolean;
  @Prop({ default: 'EnrichmentErrors' }) public errorFilename!: string;
  @Prop() public enrichedFileName!: string;

  @Inject('restart') public restart!: RestartFunction;
  @Inject('getService') public getService!: GetService;
  @Inject('setMapping') public setMapping!: SetMapping;
  @Inject('getUser') public getUser!: GetUserFunction;
  @Inject('setFileSource') public setFileSource!: SetFileSource;
  @Inject('next') public next!: NextStepFunction;

  @InjectReactive() public readonly extraParams!: Record<string, string>;
  @InjectReactive() public readonly fileSource!: IFileSource | null;
  @InjectReactive() public readonly mapping!: IMapping<IMappingDetail> | null;

  /**
   * The wrapper for the component instance
   */
  public get component(): IStepComponent | null {
    return {
      isValid: this.isValid,
      isDirty: this.isDirty,
      isReadonly: this.isReadonly,
      clear: this.clear,
      initializeEvents: this.initializeEvents,
      clearEvents: this.clearEvents,
      initialize: this.initialize,
      enter: this.enter,
      skip: this.skip,
      leave: this.leave
    };
  };

  public get innerValue(): string { return this.value; }
  public set innerValue(val: string) {
    this.$emit('input', val);
  }

  private verificationToken: string = '';

  public async mounted() {
    this.verificationToken = await this.$verificationToken();
  }

  public async clear(): Promise<void> {
    this.isValid = true;
    this.isDirty = false;
    this.isReadonly = false;

    if (!this.isEnriched) {
      this.description = '';
    }
    this.isImportDisabled = false;

    this.existingLoanFound = null;
    this.insertCount = 0;
    this.updateCount = 0;
    this.deleteCount = 0;
    this.loanListCache = [];
    this.duplicateLoanDataCache = [];
  }

  public async initialize(): Promise<boolean> {
    return true;
  }

  public async enter(): Promise<void> {
    // Nothing needs to happen during enter
  }

  public async skip(): Promise<void> {
    // On Skip
  }

  /**
   * Intialize events for the event bus
   **/
  public async initializeEvents(): Promise<void> {
    // Do Nothing
  }

  /**
   * Remove events from the event bus
   **/
  public async clearEvents(): Promise<void> {
    // Do Nothing
  }

  public async leave(dir: 1 | -1): Promise<boolean> {
    return true;
  }

  public gotoTransaction() {
    window.location.href = `/TransactionManagement/${this.packageId}?openLoans=true`;
  }

  public async importData() {
    const dataImportService = this.getService('dataImport');
    if (this.scratchPadNode.length > 0 && dataImportService !== null) {
      try {
        if (this.mapping !== null) {
          this.$loading('Updating Mapping');
          await this.updateMapping();
        }

        this.$loading('Promoting Enriched Data');
        const result = await this.promoteNode();
        if (!Array.isArray(result)) {
          this.insertCount = result.countDetails.insertCount;
          this.updateCount = result.countDetails.updateCount;
          this.deleteCount = result.countDetails.deleteCount;

          this.loanListCache = result.loanList ?? [];
          this.duplicateLoanDataCache = [];

          this.existingLoanFound = false;
          this.description = 'Imported Successfully!';
          this.isValid = true;

          this.$alert.success('Your data has been successfully imported.');
          await this.next(null);
        } else {
          this.errorMessages = groupBy(result, x => x.errorGroup);
          this.selectedErrorMessages = this.errorMessages.length > 0 ? this.errorMessages[0].value : [];

          this.errorGridKey += 1;
          this.existingLoanFound = true;
          this.isImportDisabled = true;
          this.description = 'Import Unsuccessful...';
          this.isValid = false;
          this.$alert.error('Your data failed to import.', 'Please See Below');

          if (this.errorMessages.length > 0) {
            const rowNode = this.errorGrid()?.gridApi?.getRowNode(this.errorMessages[0].key);
            rowNode?.setSelected(true, true);
          }
        }
      } catch (err: any) {
        this.$log('Import Enrichment Failed: ', err);
        if (err.isAxiosError && err.response?.data?.Messages) {
          this.$alert({
            type: 'error',
            title: 'The following errors occurred!',
            errors: err.response.data.Messages
          });
        } else {
          this.$alert.error('Error occurred during import process');
        }
      } finally {
        this.$loading();
      }
    } else {
      this.$alert.warning('No New Data To Import');
    }
  }

  public onErrorMessageSelected(e: SelectionChangedEvent) {
    const data = e.api.getSelectedRows() as IKeyValue<string, IDataPreviewError[]>[];
    this.selectedErrorMessages = data.length > 0 && data[0].value.length > 0 ? data[0].value : [];
    this.selectedErrorGridKey++;
  }

  /**
     * Method is meant to return the menu items for the context menu
     * 
     * @param params The paramters that are sent to the context menu on building
     */
  public contextMenuItems(params: GetContextMenuItemsParams) {
    const defaultMenu: (string | MenuItemDef)[] = ['copy', 'copyWithHeaders', 'paste'];
    return defaultMenu;
  }

  /**
   * Export out all the error file based on the messages brought back from the server side
   */
  public exportErrorData() {
    const errors = this.errorMessages
      .reduce((a, x) => a.concat(x.value.map(y => ({
        'Message': y.errorDesc,
        'Loan Id': y.requiredLoanId,
        'Internal Field': y.internalFieldName,
        'Group': y.errorGroup
      }))), [] as any[]);

    const ws = XLSX.utils.json_to_sheet(errors);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, `${this.errorFilename}.xlsx`);
  }

  @Watch('isEnriched', { immediate: true })
  public onIsEnrichedChange(newIsEnriched: boolean) {
    if (newIsEnriched)
      this.description = 'Imported Successfully!';
    else
      this.description = '';
  }

  private async promoteNode(): Promise<ILoanImport | IDataPreviewError[]> {
    /// TODO: This needs to be moved to a service in the query builder npm package instead of being here
    const data = {
      nodeId: this.scratchPadNode,
      fileName: this.enrichedFileName,
      packageId: this.packageId,
      createdBy: this.getUser(),
      versionName: this.innerValue,
      domainName: 'loan',
      enrichmentId: this.enrichment.id,
      enrichmentPath: this.enrichment.enrichmentPath
    };

    const helper = new AjaxHelper(this.scratchPadUrl, this.verificationToken, null, this.$token);
    return await helper.postData<ILoanImport | IDataPreviewError[]>('/api/Scratchpad/Publish', data, {});
  }

  /**
   * Helper method to update the mapping internally with the enrichment and validation ids
   **/
  private async updateMapping() {
    const service = this.getService('dataImport');
    if (service !== null && this.mapping !== null) {
      const updateMapping = JSON.parse(JSON.stringify(this.mapping)) as IMapping<IMappingDetail>;
      if (this.validation !== null)
        updateMapping.validationTemplateId = this.validation.id;
      updateMapping.details = [];

      const newMapping = await service.updateMapping(updateMapping);
      if (newMapping !== null) {
        this.setMapping(newMapping);
      }
    }
  }
}